import React, { useState } from 'react';
import { 
  Inbox, Kanban, Users, CheckSquare, Search, Filter, 
  Phone, MessageCircle, Link as LinkIcon, Clock, MoreVertical,
  Plus, Calendar, Activity, CheckCircle2, ChevronRight, LogOut, ArrowUpRight
} from 'lucide-react';
import { auth } from '../../lib/api';

// --- MOCK DATA PARA VISUALIZAÇÃO ---
const MOCK_LEADS = [
  { id: '1', name: 'Dr. Carlos Mendes', segment: 'Odontologia', status: 'novo', temp: 'quente', lastAction: '10 min atrás', phone: '(11) 99999-1111', site: 'nyroh.com/carlos-mendes' },
  { id: '2', name: 'Dra. Ana Silva', segment: 'Advocacia', status: 'contato', temp: 'morno', lastAction: '2 horas atrás', phone: '(11) 98888-2222', site: 'nyroh.com/ana-silva' },
  { id: '3', name: 'Clínica Bem Estar', segment: 'Estética', status: 'proposta', temp: 'quente', lastAction: 'Ontem', phone: '(11) 97777-3333', site: 'nyroh.com/bem-estar' },
  { id: '4', name: 'João Advocacia', segment: 'Advocacia', status: 'novo', temp: 'frio', lastAction: 'Agora', phone: '(11) 96666-4444', site: '' },
  { id: '5', name: 'Sorria Mais', segment: 'Odontologia', status: 'fechado', temp: 'quente', lastAction: '3 dias atrás', phone: '(11) 95555-5555', site: 'nyroh.com/sorria-mais' },
];

export function VendedorDashboard() {
  const [activeRoute, setActiveRoute] = useState<'cockpit' | 'pipeline' | 'carteira' | 'followups'>('cockpit');
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>('1');
  const [crmTab, setCrmTab] = useState<'resumo' | 'historico' | 'financeiro'>('resumo');

  const selectedLead = MOCK_LEADS.find(l => l.id === selectedLeadId);

  const handleLogout = async () => {
    await auth.signOut({});
  };

  return (
    <div className="flex h-screen w-full bg-white overflow-hidden font-sans text-slate-900 selection:bg-indigo-100">
      
      {/* ================= SIDEBAR (Menu Lateral) ================= */}
      <div className="w-64 border-r border-slate-200 bg-slate-50/50 flex flex-col justify-between flex-shrink-0">
        <div>
          {/* Logo / Header Sidebar */}
          <div className="h-14 flex items-center px-6 border-b border-slate-200 bg-white">
            <div className="w-6 h-6 bg-indigo-600 rounded flex items-center justify-center mr-3">
              <span className="text-white text-xs font-black">N</span>
            </div>
            <span className="font-bold tracking-tight">Workspace</span>
          </div>

          {/* Navegação Principal */}
          <nav className="p-4 space-y-1">
            <p className="px-3 text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 mt-2">Mesa de Operações</p>
            
            <button 
              onClick={() => setActiveRoute('cockpit')}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeRoute === 'cockpit' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              <Inbox size={18} className={activeRoute === 'cockpit' ? 'text-indigo-600' : 'text-slate-400'} />
              Cockpit (Inbox)
              <span className="ml-auto bg-indigo-100 text-indigo-600 py-0.5 px-2 rounded-full text-xs font-bold">2</span>
            </button>
            
            <button 
              onClick={() => setActiveRoute('pipeline')}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeRoute === 'pipeline' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              <Kanban size={18} className={activeRoute === 'pipeline' ? 'text-indigo-600' : 'text-slate-400'} />
              Pipeline (Funil)
            </button>
            
            <button 
              onClick={() => setActiveRoute('carteira')}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeRoute === 'carteira' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              <Users size={18} className={activeRoute === 'carteira' ? 'text-indigo-600' : 'text-slate-400'} />
              Base de Clientes
            </button>
            
            <button 
              onClick={() => setActiveRoute('followups')}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeRoute === 'followups' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              <CheckSquare size={18} className={activeRoute === 'followups' ? 'text-indigo-600' : 'text-slate-400'} />
              Follow-ups
            </button>
          </nav>
        </div>

        {/* User Footer Sidebar */}
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-600 text-sm">
              V
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900 truncate">Vendedor 01</p>
              <p className="text-xs text-slate-500 truncate">Online</p>
            </div>
            <button onClick={handleLogout} className="text-slate-400 hover:text-red-500 transition-colors p-1">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ================= MAIN CONTENT AREA ================= */}
      <div className="flex-1 flex flex-col h-full bg-white relative">
        
        {/* Top Status Bar (Discreet Metrics) */}
        <div className="h-14 border-b border-slate-200 flex items-center justify-between px-6 bg-white shrink-0 z-10">
          <div className="flex items-center gap-6">
            <h1 className="font-bold text-lg capitalize flex items-center gap-2">
              {activeRoute === 'cockpit' && <><Inbox size={20} className="text-slate-400"/> Cockpit</>}
              {activeRoute === 'pipeline' && <><Kanban size={20} className="text-slate-400"/> Pipeline</>}
              {activeRoute === 'carteira' && <><Users size={20} className="text-slate-400"/> Base de Clientes</>}
              {activeRoute === 'followups' && <><CheckSquare size={20} className="text-slate-400"/> Follow-ups</>}
            </h1>
            
            <div className="hidden md:flex items-center gap-4 text-xs font-mono bg-slate-50 px-4 py-1.5 rounded-lg border border-slate-200">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500"></span> Leads: 45</span>
              <span className="text-slate-300">|</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-orange-500"></span> Quentes: 12</span>
              <span className="text-slate-300">|</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Fechados: 2</span>
              <span className="text-slate-300">|</span>
              <span className="font-bold text-slate-700">Meta: 67%</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors"><Search size={18} /></button>
            <button className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors shadow-sm">
              <Plus size={16} /> Novo Lead
            </button>
          </div>
        </div>

        {/* Content Area Routing */}
        <div className="flex-1 overflow-hidden relative">
          
          {/* ROTA: COCKPIT (Split-pane view) */}
          {activeRoute === 'cockpit' && (
            <div className="flex h-full w-full">
              
              {/* Cockpit - Left List (35%) */}
              <div className="w-[35%] min-w-[320px] max-w-[400px] border-r border-slate-200 bg-white flex flex-col h-full z-0">
                <div className="p-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Ação Necessária (2)</span>
                  <button className="text-slate-400 hover:text-slate-600"><Filter size={16} /></button>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                  {MOCK_LEADS.map(lead => (
                    <div 
                      key={lead.id}
                      onClick={() => setSelectedLeadId(lead.id)}
                      className={`p-4 border-b border-slate-100 cursor-pointer transition-colors relative ${selectedLeadId === lead.id ? 'bg-indigo-50/50' : 'hover:bg-slate-50'}`}
                    >
                      {selectedLeadId === lead.id && <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-600"></div>}
                      <div className="flex justify-between items-start mb-1">
                        <h3 className={`text-sm font-bold ${selectedLeadId === lead.id ? 'text-indigo-900' : 'text-slate-900'}`}>{lead.name}</h3>
                        <span className="text-[10px] text-slate-400 font-mono">{lead.lastAction}</span>
                      </div>
                      <p className="text-xs text-slate-500 mb-2">{lead.segment}</p>
                      
                      <div className="flex gap-2">
                        {lead.status === 'novo' && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">NOVO</span>}
                        {lead.status === 'proposta' && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded">PROPOSTA</span>}
                        {lead.temp === 'quente' && <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-bold rounded flex items-center gap-1"><Activity size={10}/> QUENTE</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cockpit - Right Detail (65%) */}
              <div className="flex-1 flex flex-col h-full bg-white relative">
                {selectedLead ? (
                  <>
                    {/* Header do Lead */}
                    <div className="px-8 py-6 border-b border-slate-200">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h2 className="text-2xl font-black text-slate-900 tracking-tight">{selectedLead.name}</h2>
                          <p className="text-slate-500 text-sm mt-1">{selectedLead.segment}</p>
                        </div>
                        <div className="flex gap-2">
                          <button className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors">
                            <MoreVertical size={18} />
                          </button>
                        </div>
                      </div>

                      {/* Ações Rápidas */}
                      <div className="flex gap-3 mt-4">
                        <button className="flex-1 bg-[#25D366] hover:bg-[#20bd5a] text-white py-2.5 px-4 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-colors shadow-sm">
                          <MessageCircle size={18} /> WhatsApp
                        </button>
                        <button className="flex-1 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 py-2.5 px-4 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-colors">
                          <Phone size={18} className="text-slate-400" /> Registrar Ligação
                        </button>
                      </div>
                    </div>

                    {/* Abas Horizontais do CRM */}
                    <div className="px-8 border-b border-slate-200 flex gap-6 bg-slate-50/50">
                      <button 
                        onClick={() => setCrmTab('resumo')}
                        className={`py-3 text-sm font-bold border-b-2 transition-colors ${crmTab === 'resumo' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                      >
                        Visão Geral & Site
                      </button>
                      <button 
                        onClick={() => setCrmTab('historico')}
                        className={`py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${crmTab === 'historico' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                      >
                        Histórico <span className="bg-slate-200 text-slate-600 text-[10px] px-1.5 py-0.5 rounded-full">3</span>
                      </button>
                      <button 
                        onClick={() => setCrmTab('financeiro')}
                        className={`py-3 text-sm font-bold border-b-2 transition-colors ${crmTab === 'financeiro' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                      >
                        Financeiro & Proposta
                      </button>
                    </div>

                    {/* Conteúdo das Abas (Scrollável) */}
                    <div className="flex-1 overflow-y-auto p-8 bg-slate-50/30">
                      
                      {crmTab === 'resumo' && (
                        <div className="space-y-6">
                          {/* Card do Site Gerado */}
                          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2">
                              <LinkIcon size={14} /> Site do Cliente (ZIP)
                            </h4>
                            {selectedLead.site ? (
                              <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-100">
                                <a href={`https://${selectedLead.site}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 font-mono text-sm hover:underline flex items-center gap-2">
                                  {selectedLead.site} <ArrowUpRight size={14} />
                                </a>
                                <button className="text-xs font-bold bg-white border border-slate-200 px-3 py-1.5 rounded hover:bg-slate-50 transition-colors">
                                  Copiar Link
                                </button>
                              </div>
                            ) : (
                              <div className="text-sm text-slate-500 italic bg-slate-50 p-4 rounded-lg border border-slate-100 text-center">
                                Nenhum site vinculado a este lead ainda.
                              </div>
                            )}
                          </div>

                          {/* Dados Cadastrais Grid */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">Telefone</span>
                              <span className="font-medium text-slate-900">{selectedLead.phone}</span>
                            </div>
                            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">Origem</span>
                              <span className="font-medium text-slate-900">Campanha Meta Ads</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {crmTab === 'historico' && (
                        <div className="space-y-4">
                          <div className="relative pl-6 border-l-2 border-slate-200 pb-4">
                            <div className="absolute w-3 h-3 bg-indigo-600 rounded-full -left-[7px] top-1"></div>
                            <p className="text-xs text-slate-400 font-mono mb-1">Hoje, 14:02</p>
                            <p className="text-sm font-medium text-slate-900 bg-white p-3 border border-slate-200 rounded-lg shadow-sm">
                              Cliente pediu para retornar amanhã no período da tarde. Link do site já foi enviado no Whats.
                            </p>
                          </div>
                          <div className="relative pl-6 border-l-2 border-slate-200 pb-4">
                            <div className="absolute w-3 h-3 bg-slate-300 rounded-full -left-[7px] top-1"></div>
                            <p className="text-xs text-slate-400 font-mono mb-1">Ontem, 09:15</p>
                            <p className="text-sm font-medium text-slate-900 bg-slate-50 p-3 border border-slate-200 rounded-lg">
                              Status alterado para: Em Negociação
                            </p>
                          </div>
                        </div>
                      )}

                      {crmTab === 'financeiro' && (
                        <div className="text-center py-12">
                          <CheckCircle2 size={40} className="text-slate-300 mx-auto mb-3" />
                          <p className="text-slate-500 font-medium">Nenhuma fatura gerada para este lead.</p>
                          <button className="mt-4 text-sm font-bold bg-white border border-slate-200 px-4 py-2 rounded-lg hover:bg-slate-50 text-indigo-600 transition-colors shadow-sm">
                            Criar Nova Fatura
                          </button>
                        </div>
                      )}

                    </div>

                    {/* Input de Anotação Rápida (Fixo no rodapé) */}
                    <div className="p-4 border-t border-slate-200 bg-white">
                      <div className="relative">
                        <input 
                          type="text" 
                          placeholder="Digite uma anotação rápida e aperte Enter..." 
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-4 pr-12 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
                        />
                        <button className="absolute right-2 top-2 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                          <ArrowRight size={16} />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                    <Inbox size={48} className="mb-4 text-slate-200" />
                    <p className="font-medium text-slate-500">Selecione um lead na lista para iniciar o atendimento.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* OUTRAS ROTAS (Apenas placeholders para demonstrar a estrutura completa) */}
          {activeRoute === 'pipeline' && (
            <div className="p-8 h-full bg-slate-50 overflow-x-auto">
              <div className="flex gap-6 min-w-max h-full">
                {/* Coluna 1 */}
                <div className="w-72 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-sm uppercase tracking-wider text-slate-600">Novos <span className="text-slate-400 ml-1">(2)</span></h3>
                  </div>
                  <div className="flex-1 space-y-3">
                    {MOCK_LEADS.filter(l => l.status === 'novo').map(lead => (
                      <div key={lead.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm cursor-grab hover:border-indigo-300 transition-colors">
                        <h4 className="font-bold text-sm text-slate-900">{lead.name}</h4>
                        <p className="text-xs text-slate-500 mt-1">{lead.segment}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Coluna 2 */}
                <div className="w-72 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-sm uppercase tracking-wider text-slate-600">Em Contato <span className="text-slate-400 ml-1">(1)</span></h3>
                  </div>
                  <div className="flex-1 space-y-3">
                    {MOCK_LEADS.filter(l => l.status === 'contato').map(lead => (
                      <div key={lead.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm cursor-grab hover:border-indigo-300 transition-colors">
                        <h4 className="font-bold text-sm text-slate-900">{lead.name}</h4>
                        <p className="text-xs text-slate-500 mt-1">{lead.segment}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="w-72 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-sm uppercase tracking-wider text-slate-600">Proposta <span className="text-slate-400 ml-1">(1)</span></h3>
                  </div>
                  <div className="flex-1 space-y-3">
                    {MOCK_LEADS.filter(l => l.status === 'proposta').map(lead => (
                      <div key={lead.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm cursor-grab hover:border-indigo-300 transition-colors border-l-2 border-l-purple-500">
                        <h4 className="font-bold text-sm text-slate-900">{lead.name}</h4>
                        <p className="text-xs text-slate-500 mt-1">{lead.segment}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeRoute === 'carteira' && (
            <div className="p-8">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Cliente</th>
                      <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Segmento</th>
                      <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Telefone</th>
                      <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {MOCK_LEADS.map(lead => (
                      <tr key={lead.id} className="hover:bg-slate-50 transition-colors cursor-pointer">
                        <td className="px-6 py-4 text-sm font-bold text-slate-900">{lead.name}</td>
                        <td className="px-6 py-4 text-sm text-slate-500">{lead.segment}</td>
                        <td className="px-6 py-4 text-sm text-slate-500 font-mono">{lead.phone}</td>
                        <td className="px-6 py-4">
                          <span className="px-2.5 py-1 text-xs font-bold rounded-md bg-slate-100 text-slate-600 capitalize">
                            {lead.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
